import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, IconButton, Collapse, Chip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { formatCellValue, renderAuthStatus, renderCanceladoERP, renderStatusColor, renderEstatusChip, renderStatusBorderColor, getStatusMobileTheme } from '../utils/formatters';
import { AdminPaymentStatusCell } from './AdminPaymentStatusCell';

const pickFirst = (obj, keys) => {
  if (!obj) return null;
  for (const k of keys) {
    const val = obj[k];
    if (val !== undefined && val !== null && val !== '') return val;
  }
  return null;
};

const renderMobileValue = (k, row, labels) => {
  if (k === 'OC_REQUI') {
    const folio = pickFirst(row, ['FOLIO', 'FOLIO_OC', 'ORDEN']);
    const requi = pickFirst(row, ['REQUI']);
    const empresa = pickFirst(row, ['EMPRESA', 'NOMBRE_EMPRESA']);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
          {folio || '-'} {requi ? `• Req: ${requi}` : ''}
        </Typography>
        {empresa && (
          <Typography variant="caption" sx={{ color: '#0284c7', fontWeight: 750, fontSize: '0.68rem' }}>
            {empresa}
          </Typography>
        )}
      </Box>
    );
  }

  if (k === 'FECHA_SOLICITANTE') {
    const fecha = pickFirst(row, ['FECHA', 'FECHA_OC', 'FECHA_ORDENCOMPRA', 'FECHASOLICITUD']);
    const sol = pickFirst(row, ['REQUISITOR', 'SOLICITANTE']);
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{fecha ? formatCellValue('FECHA', fecha) : '-'}</Typography>
        {sol && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.68rem', textTransform: 'uppercase' }}>{sol}</Typography>}
      </Box>
    );
  }

  if (k === 'PROVEEDOR_COMPRADOR') {
    const prov = pickFirst(row, ['PROVEEDOR']);
    const comp = pickFirst(row, ['COMPRADOR', 'COMPRADORA', 'NOMBRE_COMPRADOR']) || pickFirst(row, ['SOLICITANTE']);
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', fontSize: '0.75rem', textTransform: 'uppercase' }}>{prov || '-'}</Typography>
        {comp && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.68rem', textTransform: 'uppercase' }}>Comp: {comp}</Typography>}
      </Box>
    );
  }

  if (k === 'CENTRO_DEPTO') {
    const cc = pickFirst(row, ['CENTROCOSTO', 'CENTROCOSTOS', 'NOMBRE_CC']);
    const depto = pickFirst(row, ['DEPARTAMENTO']);
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{cc || '-'}</Typography>
        {depto && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.68rem' }}>{depto}</Typography>}
      </Box>
    );
  }

  if (k === 'TOTAL_DIVISA') {
    const val = pickFirst(row, ['TOTAL_OC', 'TOTAL', 'TOTALOC', 'MONTO']);
    const total = formatCellValue('TOTAL', val);
    const divisa = pickFirst(row, ['DIVISA', 'MONEDA']) || 'MXN';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.8rem' }}>
          {total || '$0.00'}
        </Typography>
        <Chip label={divisa} size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 800, bgcolor: '#e0f2fe', color: '#0369a1' }} />
      </Box>
    );
  }

  if (k === 'MONTOS_RESUMEN') {
    const solVal = pickFirst(row, ['MONTOSOLICITADO', 'MONTO_SOLICITADO', 'SOLICITADO']);
    const pagVal = pickFirst(row, ['PAGO_ACUM', 'PAGADO', 'ABONOS']);
    const penVal = pickFirst(row, ['PENDIENTE', 'RESTANTE']);

    const sol = formatCellValue('MONTOSOLICITADO', solVal);
    const pag = formatCellValue('PAGADO', pagVal);
    const pen = formatCellValue('PENDIENTE', penVal);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
        <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700, fontSize: '0.68rem' }}>
          Sol: {sol || '$0.00'}
        </Typography>
        {pagVal != null && Number(String(pagVal).replace(/[^\d.-]/g, '')) > 0 && (
          <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700, fontSize: '0.68rem' }}>
            Pag: {pag || '$0.00'}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 700, fontSize: '0.68rem' }}>
          Pend: {pen || '$0.00'}
        </Typography>
      </Box>
    );
  }

  if (k === 'FACTURA_PAGO') {
    const fac = pickFirst(row, ['FOLIO_FACTURA', 'FACTURA']);
    const pago = pickFirst(row, ['CONDICION_PAGO', 'PAGO', 'TIPO_PAGO']);
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{fac || 'Sin Factura'}</Typography>
        {pago && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.68rem' }}>{pago}</Typography>}
      </Box>
    );
  }

  if (k === 'MONTO_RESTANTE') {
    const sol = formatCellValue('MONTOSOLICITADO', pickFirst(row, ['MONTOSOLICITADO', 'MONTO_SOLICITADO']));
    const pen = formatCellValue('PENDIENTE', pickFirst(row, ['PENDIENTE', 'RESTANTE', 'MONTO_RESTANTE', 'PAGO_ERP_SALDO']));
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
        <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700, fontSize: '0.68rem' }}>Sol: {sol || '$0.00'}</Typography>
        <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 700, fontSize: '0.68rem' }}>Pend: {pen || '$0.00'}</Typography>
      </Box>
    );
  }

  if (k === 'FACTURA_COMPACTA') {
    const fac = pickFirst(row, ['FOLIO_FACTURA', 'FACTURA', 'FACTURAS_ASOCIADAS']);
    const pago = pickFirst(row, ['FECHAPAGO', 'FECHA_PAGO', 'CONDICION_PAGO', 'PAGO']);
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{fac || 'Sin Factura'}</Typography>
        {pago && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.68rem' }}>{pago}</Typography>}
      </Box>
    );
  }

  if (k === 'EXHIBIT' || k === 'EXHIBICION') {
    const rawExhibit = pickFirst(row, ['EXHIBIT', 'EXHIBICION', 'exhibit', 'exhibicion']);
    const exhibit = rawExhibit ? String(rawExhibit).replace(/^Solicitud\s*/i, '').trim() : '';
    return <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{exhibit || '-'}</Typography>;
  }

  if (k === 'SOLICITUD_RESUMEN') return <AdminPaymentStatusCell type="solicitud" row={row} />;
  if (k === 'REPORTE_SOLICITADO_RESUMEN') return <AdminPaymentStatusCell type="reporte" row={row} />;
  if (k === 'ENTRADA_RESUMEN') return <AdminPaymentStatusCell type="entrada" row={row} />;
  if (k === 'PAGO_ERP_RESUMEN') return <AdminPaymentStatusCell type="comprobante" row={row} />;
  if (k === 'PAGO_RESUMEN') return <AdminPaymentStatusCell type="pago" row={row} />;

  if (k === 'ESTATUS_COMPACTO' || k === 'ESTATUS') {
    return renderEstatusChip(row.ESTATUS);
  }

  if (k.startsWith('AUTNIV')) return renderAuthStatus(row[k], row.MOTIVOCANCELACION);
  if (k === 'CANCELADO_ERP') return renderCanceladoERP(row[k]);
  return formatCellValue(k, row[k]);
};

export const MobileCardRow = React.memo(({
  row,
  orderedKeys,
  labels,
  renderRowActions,
  hasPartidas,
  onRowClick,
  onAdminStatusClick
}) => {
  const [expanded, setExpanded] = useState(false);
  const status = pickFirst(row, ['ESTATUS', 'ESTATUS_COMPACTO']);
  const confirmarPrecio = row.CONFIRMARPRECIO;
  const canceladoErp = row.CANCELADO_ERP;
  const theme = getStatusMobileTheme(status, confirmarPrecio, canceladoErp);

  // Detección de si la fila tiene estatus administrativos (SolicitudesAdmin)
  const hasAdminStatuses = Boolean(
    onAdminStatusClick ||
    row.SOLICITUD_RESUMEN !== undefined ||
    row.PAGO_RESUMEN !== undefined ||
    row.ENTRADA_RESUMEN !== undefined ||
    row.PAGO_ERP_RESUMEN !== undefined ||
    row.REPORTE_SOLICITADO_RESUMEN !== undefined ||
    row.SOLICITUDES_COUNT !== undefined ||
    row.ENTRADAS_COUNT !== undefined ||
    row.PAGO_ERP_SALDO !== undefined ||
    row.REPORTE_SOLICITADO_COUNT !== undefined
  );

  const partidasList = (Array.isArray(row?.partidas) && row.partidas.length > 0)
    ? row.partidas
    : (Array.isArray(row?.PARTIDAS) && row.PARTIDAS.length > 0)
      ? row.PARTIDAS
      : [];

  const folio = pickFirst(row, ['ORDEN', 'FOLIO', 'FOLIO_OC', 'OC', 'ID_SOLICITUD', 'ID']);
  const requi = pickFirst(row, ['REQUI', 'REQUISICION', 'FOLIO_REQUI']);
  const isSolicitudRow = Boolean(
    row.OC_REQUI !== undefined ||
    folio ||
    hasAdminStatuses ||
    row.ORDEN ||
    row.TOTAL_OC !== undefined ||
    row.PROVEEDOR !== undefined ||
    row.MONTOSOLICITADO !== undefined
  );

  // Solicitudes-specific fields
  const empresa = pickFirst(row, ['EMPRESA', 'NOMBRE_EMPRESA', 'EMPRESA_NOMBRE']);
  const totalVal = pickFirst(row, ['TOTAL_OC', 'TOTAL', 'TOTALOC', 'MONTO', 'TOTAL_ORDEN']);
  const total = formatCellValue('TOTAL', totalVal);
  const divisa = String(pickFirst(row, ['DIVISA', 'MONEDA', 'moneda']) || 'MXN').toUpperCase().trim();
  const solVal = pickFirst(row, ['MONTOSOLICITADO', 'MONTO_SOLICITADO', 'SOLICITADO', 'PAGO_OPERATIVO_TOTAL']);
  const sol = formatCellValue('MONTOSOLICITADO', solVal);
  const penVal = pickFirst(row, ['PENDIENTE', 'RESTANTE', 'MONTO_RESTANTE', 'PAGO_ERP_SALDO']);
  const pen = formatCellValue('PENDIENTE', penVal);
  const prov = pickFirst(row, ['PROVEEDOR', 'PROVEEDOR_NOMBRE', 'NOMBRE_PROVEEDOR']);
  const fecha = pickFirst(row, ['FECHA_OC', 'FECHASOLICITUD', 'FECHA_SOLICITUD', 'FECHA', 'FECHA_ORDENCOMPRA', 'FECHACREACION', 'FECHA_EMISION']);
  const numCc = pickFirst(row, ['NUMERO_CC', 'NUMEROCC', 'CC']);
  const nombreCc = pickFirst(row, ['NOMBRE_CC', 'CENTROCOSTO', 'CENTRO_COSTO', 'CENTROCOSTOS']);
  const cc = (numCc && nombreCc && !nombreCc.includes(numCc)) ? `${numCc} - ${nombreCc}` : (nombreCc || numCc);
  const depto = pickFirst(row, ['DEPARTAMENTO', 'NOMBRE_DEPARTAMENTO']);
  const numEco = pickFirst(row, ['NUMERO_ECONOMICO', 'NUMERO_ECO', 'ECONOMICO', 'ECO', 'ECO_EQUIPO', 'EQUIPO']);
  const fac = pickFirst(row, ['FACTURAS_ASOCIADAS', 'FOLIO_FACTURA', 'FACTURA']);
  const pago = pickFirst(row, ['CONDICION_PAGO', 'PAGO', 'TIPO_PAGO', 'METODOPAGO']);
  const rawExhibit = pickFirst(row, ['EXHIBIT', 'EXHIBICION', 'exhibit', 'exhibicion']);
  const exhibit = rawExhibit ? String(rawExhibit).replace(/^Solicitud\s*/i, '').trim() : '';
  const notasReq = pickFirst(row, ['NOTAS_REQUI', 'NOTASREQUISICION', 'NOTAS_REQUISICION', 'NOTAS']);
  const descRaw = pickFirst(row, ['DESCRIPCION', 'CONCEPTO', 'DESCRIPCION_CONCEPTO', 'OBSERVACIONES']);
  const veces = Number(pickFirst(row, ['VECES_REPORTADO', 'vecesReportado', 'REPORTE_SOLICITADO_COUNT', 'VECES']) || 0);

  // Detección robusta de Comprador y Requisitor:
  // En este ERP:
  // - REQUISITOR contiene al usuario requisitor
  // - SOLICITANTE contiene frecuentemente al comprador cuando COMPRADOR viene vacío
  const explicitComp = pickFirst(row, ['COMPRADOR', 'COMPRADORA', 'NOMBRE_COMPRADOR', 'COMPRADOR_ERP']);
  const explicitReq = pickFirst(row, ['REQUISITOR', 'NOMBRE_REQUISITOR', 'USUARIO_REQUI', 'SOLICITANTE_REQUI']);
  const solicitanteVal = pickFirst(row, ['SOLICITANTE']);

  let comp = explicitComp;
  let solicitante = explicitReq;

  if (explicitReq) {
    if (!comp && solicitanteVal && solicitanteVal !== explicitReq) {
      comp = solicitanteVal;
    }
  } else if (explicitComp) {
    if (!solicitante && solicitanteVal && solicitanteVal !== explicitComp) {
      solicitante = solicitanteVal;
    }
  } else {
    // Si no hay explicitReq ni explicitComp, en Solicitudes/SolicitudesProgramadas SOLICITANTE = Comprador
    comp = solicitanteVal;
  }

  // Generic fallback split
  const keyKeys = orderedKeys.slice(0, 4); 
  const restKeys = orderedKeys.slice(4);

  return (
    <Card 
      sx={{ 
        mb: 1.5, 
        background: theme.gradient,
        borderRadius: '14px',
        border: `1px solid ${theme.border}`,
        borderLeft: `5px solid ${theme.primary}`,
        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.05)',
        transition: 'all 0.15s ease-in-out',
        '&:active': { transform: 'scale(0.995)' }
      }}
      onClick={(e) => {
        const t = e.target;
        if (t.closest('button') || t.closest('a') || t.closest('input')) return;
        if (onRowClick) onRowClick(row);
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {isSolicitudRow ? (
          <>
            {/* ─── Encabezado de Solicitud: Folio, Requi, Estatus, Empresa y Acciones ─── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0, pr: 0.8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 900, color: '#0f172a', fontSize: '0.92rem', letterSpacing: '-0.01em' }}>
                    {folio || '-'} {requi ? `• Req: ${requi}` : ''}
                  </Typography>
                  {status && renderEstatusChip(status)}
                  {veces > 1 && (
                    <Chip
                      label={`Solicitada ${veces} veces`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.62rem',
                        fontWeight: 850,
                        bgcolor: '#fef3c7',
                        color: '#b45309',
                        border: '1px solid #fde68a',
                        borderRadius: '6px'
                      }}
                    />
                  )}
                </Box>
                {(empresa || fecha) && (
                  <Typography sx={{ color: '#0284c7', fontWeight: 750, fontSize: '0.70rem', mt: 0.2 }}>
                    {empresa}{empresa && fecha ? ' • ' : ''}{fecha ? formatCellValue('FECHA', fecha) : ''}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
                {renderRowActions && renderRowActions(row)}
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.92)',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '8px',
                    p: 0.35,
                    color: theme.primary,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                    '&:hover': { bgcolor: '#ffffff' }
                  }}
                >
                  {expanded ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>
            </Box>

            {/* ─── Franja Destacada de Importes y Divisa (Visible Colapsada) ─── */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'rgba(255, 255, 255, 0.88)',
                backdropFilter: 'blur(4px)',
                px: 1.2,
                py: 0.7,
                borderRadius: '10px',
                border: `1px solid ${theme.border}`,
                mb: 1,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.02)',
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '0.60rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  {totalVal ? 'Total OC' : 'Pago Solicitado'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Typography sx={{ fontSize: '0.90rem', fontWeight: 900, color: '#0f172a' }}>
                    {total || sol || '$0.00'}
                  </Typography>
                  <Chip
                    label={divisa}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 850,
                      bgcolor: divisa === 'USD' ? '#fef3c7' : divisa === 'EUR' ? '#e0e7ff' : '#e0f2fe',
                      color: divisa === 'USD' ? '#b45309' : divisa === 'EUR' ? '#4338ca' : '#0369a1',
                      borderRadius: '4px'
                    }}
                  />
                </Box>
              </Box>

              {(solVal || penVal) && (
                <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                  {solVal && (
                    <Typography sx={{ fontSize: '0.70rem', fontWeight: 750, color: '#166534' }}>
                      Sol: <span style={{ fontWeight: 850 }}>{sol}</span>
                    </Typography>
                  )}
                  {penVal && (
                    <Typography sx={{ fontSize: '0.70rem', fontWeight: 750, color: '#b45309' }}>
                      Pend: <span style={{ fontWeight: 850 }}>{pen}</span>
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* ─── Franja de Estatus Administrativos (Solicitud, Pagado, Comprobante, Entrada) ─── */}
            {hasAdminStatuses && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '6px 8px',
                  p: 0.9,
                  mb: 1,
                  bgcolor: '#ffffff',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
                }}
              >
                {/* 1. SOLICITUD */}
                <Box sx={{ borderRight: '1px solid #f1f5f9', pr: 0.6, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em', mb: 0.2 }}>
                    SOLICITUD
                  </Typography>
                  <AdminPaymentStatusCell type="solicitud" row={row} onClick={onAdminStatusClick} />
                </Box>

                {/* 2. PAGADO */}
                <Box sx={{ pl: 0.4, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em', mb: 0.2 }}>
                    PAGADO
                  </Typography>
                  <AdminPaymentStatusCell type="pago" row={row} onClick={onAdminStatusClick} />
                </Box>

                {/* 3. COMPROBANTE */}
                <Box sx={{ borderRight: '1px solid #f1f5f9', pr: 0.6, borderTop: '1px solid #f1f5f9', pt: 0.6, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em', mb: 0.2 }}>
                    COMPROBANTE
                  </Typography>
                  <AdminPaymentStatusCell type="comprobante" row={row} onClick={onAdminStatusClick} />
                </Box>

                {/* 4. ENTRADA */}
                <Box sx={{ pl: 0.4, borderTop: '1px solid #f1f5f9', pt: 0.6, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em', mb: 0.2 }}>
                    ENTRADA
                  </Typography>
                  <AdminPaymentStatusCell type="entrada" row={row} onClick={onAdminStatusClick} />
                </Box>

                {/* 5. SOLICITAR PAGO */}
                <Box sx={{ gridColumn: 'span 2', borderTop: '1px solid #f1f5f9', pt: 0.6, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em', mb: 0.2 }}>
                    SOLICITAR PAGO
                  </Typography>
                  <AdminPaymentStatusCell type="reporte" row={row} onClick={onAdminStatusClick} />
                </Box>
              </Box>
            )}

            {/* ─── Datos Principales Visibles: Proveedor, Centro de Costo, Económico y Departamento ─── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '6px 10px', mt: 0.8 }}>
              {/* Proveedor */}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.60rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.01em' }}>
                  Proveedor
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 750, color: '#0f172a', lineHeight: 1.25, mt: 0.15, wordBreak: 'break-word', textTransform: 'uppercase' }}>
                  {prov || '-'}
                </Typography>
              </Box>

              {/* Centro de Costo */}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.60rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.01em' }}>
                  Centro de Costo
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.25, mt: 0.15, wordBreak: 'break-word', textTransform: 'uppercase' }}>
                  {cc || '-'}
                </Typography>
              </Box>

              {/* Económico */}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.60rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.01em' }}>
                  Económico
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.25, mt: 0.15, wordBreak: 'break-word', textTransform: 'uppercase' }}>
                  {numEco || '-'}
                </Typography>
              </Box>

              {/* Departamento */}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.60rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.01em' }}>
                  Departamento
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.25, mt: 0.15, wordBreak: 'break-word', textTransform: 'uppercase' }}>
                  {depto || '-'}
                </Typography>
              </Box>
            </Box>

            {/* ─── Sección Expandida de Solicitud ─── */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1.2, pt: 1.2, borderTop: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Descripción / Concepto */}
                {descRaw && (
                  <Box sx={{ p: 0.9, bgcolor: 'rgba(255, 255, 255, 0.92)', borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                    <Typography sx={{ fontSize: '0.60rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                      Descripción / Concepto
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#0f172a', mt: 0.2, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                      {descRaw}
                    </Typography>
                  </Box>
                )}

                {/* Comprador y Requisitor */}
                {(comp || solicitante) && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, p: 0.9, bgcolor: 'rgba(255, 255, 255, 0.92)', borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.60rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                        Comprador
                      </Typography>
                      <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', mt: 0.15, wordBreak: 'break-word', textTransform: 'uppercase' }}>
                        {comp || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.60rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                        Requisitor
                      </Typography>
                      <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', mt: 0.15, wordBreak: 'break-word', textTransform: 'uppercase' }}>
                        {solicitante || '-'}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Tipo de Pago y Exhibit */}
                {(pago || exhibit) && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, p: 0.8, bgcolor: 'rgba(255, 255, 255, 0.92)', borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                        Tipo de Pago
                      </Typography>
                      <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', mt: 0.2, textTransform: 'uppercase' }}>
                        {pago || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                        Exhibit
                      </Typography>
                      <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', mt: 0.2, textTransform: 'uppercase' }}>
                        {exhibit || '-'}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Factura */}
                {fac && fac !== '-' && (
                  <Box sx={{ p: 0.8, bgcolor: 'rgba(255, 255, 255, 0.92)', borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                      Factura
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', mt: 0.2, textTransform: 'uppercase' }}>
                      {fac}
                    </Typography>
                  </Box>
                )}

                {/* Notas Requisición */}
                {notasReq && (
                  <Box sx={{ p: 0.8, bgcolor: 'rgba(255, 255, 255, 0.92)', borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                      Notas Requisición
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#334155', mt: 0.2 }}>
                      {notasReq}
                    </Typography>
                  </Box>
                )}

                {/* Niveles de Autorización si aplican */}
                {orderedKeys.filter(k => k.startsWith('AUTNIV')).length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', p: 0.8, bgcolor: 'rgba(255, 255, 255, 0.92)', borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                    {orderedKeys.filter(k => k.startsWith('AUTNIV')).map(k => (
                      <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#64748b' }}>
                          {labels[k] || k}:
                        </Typography>
                        {renderAuthStatus(row[k], row.MOTIVOCANCELACION)}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Partidas OC detalladas */}
                {hasPartidas && partidasList.length > 0 && (
                  <Box sx={{ p: 1.2, backgroundColor: theme.subtleBg, borderRadius: '10px', border: `1px solid ${theme.border}` }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#334155', mb: 0.8, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>📦</span> Partidas OC ({partidasList.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                      {partidasList.map((p, i) => {
                        const desc = pickFirst(p, ['DESCRIPCION_PART', 'DESCRIPCION', 'DESCRIPCION_PARTIDA']) || `Partida ${i + 1}`;
                        const codigo = pickFirst(p, ['CODIGO_PART', 'CODIGO', 'CODIGO_PARTIDA']);
                        const cant = formatCellValue('CANTIDAD', p.CANTIDAD);
                        const cu = formatCellValue('COSTOU', p.COSTOU);
                        const tot = formatCellValue('TOTAL_PART', p.TOTAL_PART);

                        return (
                          <Box
                            key={i}
                            sx={{
                              p: 1,
                              bgcolor: '#ffffff',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                              <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.25, flex: 1 }}>
                                <span style={{ color: '#64748b', fontWeight: 800 }}>#{p.CONSECUTIVO || i + 1} </span>
                                {desc}
                              </Typography>
                              {tot && (
                                <Typography sx={{ fontSize: '0.76rem', fontWeight: 900, color: '#0f172a', whiteSpace: 'nowrap', ml: 1 }}>
                                  {tot}
                                </Typography>
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.6, flexWrap: 'wrap' }}>
                              {codigo && (
                                <Typography sx={{ fontSize: '0.66rem', color: '#64748b' }}>
                                  Código: <span style={{ fontWeight: 600, color: '#334155' }}>{codigo}</span>
                                </Typography>
                              )}
                              {cant && (
                                <Typography sx={{ fontSize: '0.66rem', color: '#64748b' }}>
                                  Cant: <span style={{ fontWeight: 600, color: '#334155' }}>{cant}</span>
                                </Typography>
                              )}
                              {cu && (
                                <Typography sx={{ fontSize: '0.66rem', color: '#64748b' }}>
                                  P.U: <span style={{ fontWeight: 600, color: '#334155' }}>{cu}</span>
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </>
        ) : (
          /* ─── Fallback Genérico para otras tablas del sistema ─── */
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
              <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  {labels[orderedKeys[0]]}: {renderMobileValue(orderedKeys[0], row, labels)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
                {renderRowActions && renderRowActions(row)}
                {(restKeys.length > 0 || (hasPartidas && partidasList.length > 0)) && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
                    {expanded ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 0.5 }}>
              {keyKeys.slice(1).map(k => (
                <Box key={k}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.66rem', textTransform: 'uppercase', fontWeight: 700 }}>
                    {labels[k] || k}
                  </Typography>
                  <Box sx={{ mt: 0.2 }}>
                    {renderMobileValue(k, row, labels)}
                  </Box>
                </Box>
              ))}
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.2 }}>
                {restKeys.map(k => (
                  <Box key={k}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.66rem', textTransform: 'uppercase', fontWeight: 700 }}>
                      {labels[k] || k}
                    </Typography>
                    <Box sx={{ mt: 0.2, wordBreak: 'break-word' }}>
                      {renderMobileValue(k, row, labels)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </CardContent>
    </Card>
  );
}, (prev, next) => prev.row === next.row && prev.orderedKeys === next.orderedKeys);
